/**
 * The register map: clustered property pins for a marker query on a Google
 * map, an optional drawn ring (polygon) with freehand / corner drawing, and a
 * lot card in an info window when a pin is clicked. Shared by the map view
 * and the area dialog so both behave the same.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { MarkerClusterer, SuperClusterAlgorithm, type Renderer } from '@googlemaps/markerclusterer'
import { useCards, usePropertyMarkers } from '../../api/queries'
import type { PropertyMarker, PropertyMarkerQuery } from '../../api/types'
import type { ExperienceSpec } from '../../experiences/registry'
import { Alert, Skeletons } from '../../ui/kit'
import { LotCard } from './LotCard'
import { isDarkTheme, loadGoogleMaps, themeToken, type GoogleMapsLibraries } from '../../services/googleMaps'
import { ringToParam } from './location'

export type AreaRing = [number, number][]
export type DrawMode = 'freehand' | 'corners' | null
const UK_CENTER = { lat: 53, lng: -1.8 }
const UK_ZOOM = 6

export function simplify(points: AreaRing, tolerance: number): AreaRing {
  if (points.length <= 2) return points
  const sq = tolerance * tolerance
  const segDist = (p: [number, number], a: [number, number], b: [number, number]) => {
    let x = a[0], y = a[1], dx = b[0] - x, dy = b[1] - y
    if (dx !== 0 || dy !== 0) { const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy); if (t > 1) { x = b[0]; y = b[1] } else if (t > 0) { x += dx * t; y += dy * t } }
    dx = p[0] - x; dy = p[1] - y
    return dx * dx + dy * dy
  }
  const keep = new Uint8Array(points.length); keep[0] = 1; keep[points.length - 1] = 1
  const stack: Array<[number, number]> = [[0, points.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()!
    let index = 0, max = 0
    for (let i = first + 1; i < last; i++) { const d = segDist(points[i]!, points[first]!, points[last]!); if (d > max) { index = i; max = d } }
    if (max > sq) { keep[index] = 1; stack.push([first, index], [index, last]) }
  }
  return points.filter((_, i) => keep[i])
}

function fitRing(map: google.maps.Map, ring: AreaRing): void {
  if (ring.length < 3) return
  const bounds = new google.maps.LatLngBounds()
  ring.forEach(([lat, lng]) => bounds.extend({ lat, lng }))
  map.fitBounds(bounds, 48)
  google.maps.event.addListenerOnce(map, 'idle', () => { if ((map.getZoom() ?? 0) > 13) map.setZoom(13) })
}

function fitMarkers(map: google.maps.Map, rows: PropertyMarker[]): void {
  if (rows.length === 0) return
  const bounds = new google.maps.LatLngBounds()
  rows.forEach(r => bounds.extend({ lat: r.latitude, lng: r.longitude }))
  map.fitBounds(bounds, 48)
  google.maps.event.addListenerOnce(map, 'idle', () => { if ((map.getZoom() ?? 0) > 15) map.setZoom(15) })
}

function pinClass(marker: PropertyMarker): string {
  return marker.saleMethod === 'auction' ? 'is-auction' : marker.saleMethod === 'private_treaty' ? 'is-private' : 'is-unscreened'
}

function pinContent(marker: PropertyMarker): HTMLElement {
  const root = document.createElement('button')
  root.type = 'button'
  root.className = `map-pin ${pinClass(marker)}`
  root.setAttribute('aria-label', `Open ${marker.addressLine1?.trim() || 'property'}`)
  const glyph = document.createElement('span'); glyph.setAttribute('aria-hidden', 'true'); root.append(glyph)
  return root
}

function clusterRenderer(AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement): Renderer {
  return {
    render({ count, position }) {
      const root = document.createElement('button'); root.type = 'button'; root.className = 'map-cluster'; root.setAttribute('aria-label', `Zoom into ${count} properties`)
      const label = document.createElement('span'); label.textContent = String(count); root.append(label)
      return new AdvancedMarkerElement({ position, content: root, gmpClickable: true, zIndex: 1000 + count })
    },
  }
}

export interface RegisterMapHandle {
  fitRing: (ring: AreaRing) => void
  fitAll: () => void
}

export function RegisterMap({ markerQuery, ring, onRingChange, drawMode, onDrawModeChange, spec, listId, enabled = true, onSummary, fitTo, children, handleRef }: {
  markerQuery: PropertyMarkerQuery
  ring: AreaRing
  onRingChange?: (ring: AreaRing) => void
  drawMode?: DrawMode
  onDrawModeChange?: (mode: DrawMode) => void
  spec: ExperienceSpec
  listId?: string
  enabled?: boolean
  onSummary?: (summary: { located: number; total: number; unlocated: number; truncated: boolean; pending: boolean; error: boolean }) => void
  /** 'ring' keeps the drawn area in view; 'markers' fits the pins when they load. */
  fitTo: 'ring' | 'markers'
  children?: ReactNode
  handleRef?: React.MutableRefObject<RegisterMapHandle | null>
}) {
  const node = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const libs = useRef<GoogleMapsLibraries | null>(null)
  const clusterer = useRef<MarkerClusterer | null>(null)
  const pins = useRef<google.maps.marker.AdvancedMarkerElement[]>([])
  const shape = useRef<google.maps.Polygon | google.maps.Polyline | null>(null)
  const points = useRef<google.maps.marker.AdvancedMarkerElement[]>([])
  const info = useRef<google.maps.InfoWindow | null>(null)
  const ringRef = useRef<AreaRing>(ring)
  const [ready, setReady] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [popupHost, setPopupHost] = useState<HTMLDivElement | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const fitted = useRef(false)

  const query = useMemo<PropertyMarkerQuery>(() => {
    const q = { ...markerQuery }
    if (ring.length >= 3) { q.polygon = ringToParam(ring); delete q.areaId; delete q.adminAreaId; delete q.lat; delete q.lng; delete q.radiusMiles; delete q.postcode; delete q.postcodeDistrict }
    return q
  }, [markerQuery, ring])
  const markers = usePropertyMarkers(query, enabled)
  const selected = useCards({ ...query, propertyId: selectedId ?? undefined, status: 'all', limit: 1, offset: 0 }, selectedId !== null)

  useEffect(() => {
    onSummary?.({ located: markers.data?.data.length ?? 0, total: markers.data?.total ?? 0, unlocated: markers.data?.unlocated ?? 0, truncated: markers.data?.truncated ?? false, pending: markers.isPending, error: markers.isError })
  }, [markers.data, markers.isPending, markers.isError, onSummary])

  // create the map once
  useEffect(() => {
    if (!node.current || mapRef.current) return
    const target = node.current
    let disposed = false
    setError(null)
    void loadGoogleMaps().then(({ maps, marker, core, mapId }) => {
      if (disposed) return
      const map = new maps.Map(target, {
        center: UK_CENTER, zoom: UK_ZOOM, mapId, colorScheme: isDarkTheme() ? 'DARK' : 'LIGHT', backgroundColor: themeToken('--muted'),
        clickableIcons: false, fullscreenControl: false, mapTypeControl: false, streetViewControl: false, gestureHandling: 'greedy', zoomControl: true,
      })
      mapRef.current = map
      libs.current = { maps, marker, core, mapId }
      const ro = new ResizeObserver(() => google.maps.event.trigger(map, 'resize'))
      ro.observe(target)
      setReady(v => v + 1)
      google.maps.event.addListenerOnce(map, 'idle', () => fitRing(map, ringRef.current))
    }).catch((e: unknown) => { if (!disposed) setError(e instanceof Error ? e.message : 'Google Maps could not be loaded') })
    return () => { disposed = true }
  }, [])

  useEffect(() => () => {
    clusterer.current?.clearMarkers()
    info.current?.close()
    pins.current.forEach(m => { m.map = null }); points.current.forEach(m => { m.map = null }); shape.current?.setMap(null)
    if (mapRef.current) google.maps.event.clearInstanceListeners(mapRef.current)
    mapRef.current = null; libs.current = null; clusterer.current = null; info.current = null; pins.current = []; shape.current = null; points.current = []
    node.current?.replaceChildren()
  }, [])

  useEffect(() => {
    if (!handleRef) return
    handleRef.current = {
      fitRing: r => { if (mapRef.current) fitRing(mapRef.current, r) },
      fitAll: () => { if (mapRef.current && markers.data) fitMarkers(mapRef.current, markers.data.data) },
    }
    return () => { handleRef.current = null }
  }, [handleRef, markers.data])

  // the ring (polygon / polyline + corner points)
  useEffect(() => {
    ringRef.current = ring
    const map = mapRef.current, l = libs.current
    if (!map || !l) return
    shape.current?.setMap(null); points.current.forEach(p => { p.map = null }); shape.current = null; points.current = []
    if (ring.length === 0) return
    const path = ring.map(([lat, lng]) => ({ lat, lng }))
    const accent = themeToken('--accent')
    const options = { map, path, strokeColor: accent, strokeOpacity: 1, strokeWeight: 2 }
    shape.current = ring.length >= 3 ? new l.maps.Polygon({ ...options, paths: path, fillColor: accent, fillOpacity: 0.14 }) : new l.maps.Polyline(options)
    points.current = path.map(position => { const el = document.createElement('span'); el.className = 'map-area-point'; return new l.marker.AdvancedMarkerElement({ map, position, content: el, zIndex: 1200 }) })
  }, [ready, ring])

  // the pins
  useEffect(() => {
    const map = mapRef.current, l = libs.current
    info.current?.close(); info.current = null; setPopupHost(null); setSelectedId(null)
    clusterer.current?.clearMarkers(); pins.current.forEach(m => { m.map = null }); clusterer.current = null; pins.current = []
    if (!map || !l) return
    const rows = markers.data?.data ?? []
    if (markers.isError || rows.length === 0) return
    const created = rows.map(row => {
      const content = pinContent(row)
      const pin = new l.marker.AdvancedMarkerElement({ position: { lat: row.latitude, lng: row.longitude }, content, gmpClickable: true, title: row.addressLine1?.trim() || 'Property' })
      content.addEventListener('click', () => {
        info.current?.close()
        const host = document.createElement('div'); host.className = 'map-popup-host'
        const win = new l.maps.InfoWindow({ ariaLabel: row.addressLine1?.trim() || 'Selected property', content: host, disableAutoPan: false, maxWidth: 360 })
        info.current = win; setPopupHost(host); setSelectedId(row.id)
        win.addListener('closeclick', () => { if (info.current !== win) return; info.current = null; setPopupHost(null); setSelectedId(null) })
        win.open({ map, anchor: pin, shouldFocus: false })
      })
      return pin
    })
    pins.current = created
    clusterer.current = new MarkerClusterer({ map, markers: created, algorithm: new SuperClusterAlgorithm({ radius: 64, maxZoom: 16 }), renderer: clusterRenderer(l.marker.AdvancedMarkerElement) })
    if (fitTo === 'markers' && !fitted.current) { fitted.current = true; fitMarkers(map, rows) }
  }, [ready, markers.data, markers.isError, fitTo])

  // corners drawing
  useEffect(() => {
    const map = mapRef.current
    if (!map || drawMode !== 'corners' || !onRingChange) return
    const container = map.getDiv(); container.classList.add('is-plotting')
    map.setOptions({ draggable: false, disableDoubleClickZoom: true })
    const listener = map.addListener('click', (event: google.maps.MapMouseEvent) => { const ll = event.latLng; if (!ll) return; onRingChange([...ringRef.current, [ll.lat(), ll.lng()]]) })
    return () => { listener.remove(); map.setOptions({ draggable: true, disableDoubleClickZoom: false }); container.classList.remove('is-plotting') }
  }, [drawMode, ready, onRingChange])

  // freehand drawing
  useEffect(() => {
    const map = mapRef.current, l = libs.current
    if (!map || !l || drawMode !== 'freehand' || !onRingChange) return
    const container = map.getDiv(); container.classList.add('is-drawing')
    map.setOptions({ draggable: false, gestureHandling: 'none', disableDoubleClickZoom: true })
    let active = false; let pts: AreaRing = []
    const draft = new l.maps.Polyline({ map, path: [], strokeColor: themeToken('--accent'), strokeOpacity: 1, strokeWeight: 3 })
    const start = (e: google.maps.MapMouseEvent) => { if (!e.latLng) return; active = true; pts = [[e.latLng.lat(), e.latLng.lng()]]; draft.setPath(pts.map(([lat, lng]) => ({ lat, lng }))) }
    const move = (e: google.maps.MapMouseEvent) => { if (!active || !e.latLng) return; pts.push([e.latLng.lat(), e.latLng.lng()]); draft.setPath(pts.map(([lat, lng]) => ({ lat, lng }))) }
    const finish = () => { if (!active) return; active = false; draft.setMap(null); const next = simplify(pts, 0.0005); if (next.length >= 3) onRingChange(next); onDrawModeChange?.(null) }
    const listeners = [map.addListener('mousedown', start), map.addListener('mousemove', move), map.addListener('mouseup', finish)]
    window.addEventListener('mouseup', finish)
    return () => { listeners.forEach(x => x.remove()); window.removeEventListener('mouseup', finish); draft.setMap(null); map.setOptions({ draggable: true, gestureHandling: 'greedy', disableDoubleClickZoom: false }); container.classList.remove('is-drawing') }
  }, [drawMode, ready, onRingChange, onDrawModeChange])

  const item = selected.data?.items[0]
  return (
    <div className="map-wrap">
      <div ref={node} className="map-canvas" aria-label="Property map" />
      {error ? <div className="map-error"><Alert title="Google map unavailable" tight>{error}</Alert></div> : null}
      {children}
      {popupHost ? createPortal(
        <div className="map-popup">
          {selected.isPending ? <Skeletons count={1} kind="block" /> : null}
          {selected.isError ? <Alert title="Could not load the property card" tight>{selected.error.message}</Alert> : null}
          {selected.data && !item ? <Alert info tight title="Outside the current filters">This property no longer matches.</Alert> : null}
          {item ? <LotCard item={item} models={selected.data?.selectedDealModels ?? []} spec={spec} listId={listId} compact /> : null}
        </div>, popupHost) : null}
    </div>
  )
}
